<template>
  <teleport to="body" :disabled="!appendToBody">
    <transition
      :name="ns.b('fade')"
      @after-enter="afterEnter"
      @after-leave="afterLeave"
      @before-leave="beforeLeave"
    >
      <jd-overlay
        v-show="visible"
        :mask="modal"
        :overlay-class="modalClass"
        :z-index="zIndex"
        @click="onModalClick"
      >
        <jd-focus-trap
          loop
          :trapped="visible"
          :focus-trap-el="drawerRef"
          :focus-start-el="focusStartRef"
          @release-requested="onCloseRequested"
        >
          <div
            ref="drawerRef"
            aria-modal="true"
            :aria-label="title || undefined"
            :aria-labelledby="!title ? titleId : undefined"
            :aria-describedby="bodyId"
            v-bind="$attrs"
            :class="[
              ns.b(),
              transition,
              direction,
              visible && 'open',
              customClass,
            ]"
            :style="
              isHorizontal ? 'width: ' + drawerSize : 'height: ' + drawerSize
            "
            role="dialog"
            data-component-name="Drawer"
            :data-event-name="visible ? 'open' : 'close'"
            :data-event-content="visible ? '打开抽屉' : '关闭抽屉'"
          >
            <span ref="focusStartRef" :class="ns.e('sr-focus')" tabindex="-1" />
            <header v-if="withHeader" :class="ns.e('header')">
              <slot
                v-if="!$slots.title"
                name="header"
                :close="handleClose"
                :title-id="titleId"
                :title-class="[ns.e('title'), ns.em('title', titleSize)]"
              >
                <span
                  v-if="!$slots.title"
                  :id="titleId"
                  role="heading"
                  :aria-level="headerAriaLevel"
                  :class="[ns.e('title'), ns.em('title', titleSize)]"
                >
                  {{ title }}
                </span>
              </slot>
              <slot v-else name="title">
                <!-- DEPRECATED SLOT -->
              </slot>
              <button
                v-if="showClose"
                :aria-label="t('el.drawer.close')"
                :class="ns.e('close-btn')"
                type="button"
                @click="handleClose"
              >
                <jd-icon :class="ns.e('close')"><close /></jd-icon>
              </button>
            </header>
            <template v-if="rendered">
              <div :id="bodyId" :class="ns.e('body')">
                <slot />
              </div>
            </template>
            <div v-if="$slots.footer" :class="ns.e('footer')">
              <slot name="footer" />
            </div>
          </div>
        </jd-focus-trap>
      </jd-overlay>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue'
import { Close } from '@jd/jdesign-icons-vue'

import { JdOverlay } from '@jdesign-vue/components/overlay'
import JdFocusTrap from '@jdesign-vue/components/focus-trap'
import { useDialog } from '@jdesign-vue/components/dialog'
import { addUnit, isSafari } from '@jdesign-vue/utils'
import JdIcon from '@jdesign-vue/components/icon'
import { useDeprecated, useLocale, useNamespace } from '@jdesign-vue/hooks'
import { useGlobalConfig } from '@jdesign-vue/components/config-provider'
import { drawerEmits, drawerProps } from './drawer'

export default defineComponent({
  name: 'JdDrawer',
  components: {
    JdOverlay,
    JdFocusTrap,
    JdIcon,
    Close,
  },
  inheritAttrs: false,
  props: drawerProps,
  emits: drawerEmits,

  setup(props, { slots }) {
    useDeprecated(
      {
        scope: 'jd-drawer',
        from: 'the title slot',
        replacement: 'the header slot',
        version: '3.0.0',
        ref: 'https://element-plus.org/en-US/component/drawer.html#slots',
      },
      computed(() => !!slots.title)
    )
    useDeprecated(
      {
        scope: 'jd-drawer',
        from: 'custom-class',
        replacement: 'class',
        version: '2.3.0',
        ref: 'https://element-plus.org/en-US/component/drawer.html#attributes',
        type: 'Attribute',
      },
      computed(() => !!props.customClass)
    )

    const globalConfig = useGlobalConfig('drawer')

    const drawerRef = ref<HTMLElement>()
    const focusStartRef = ref<HTMLElement>()
    const ns = useNamespace('drawer')
    const { t } = useLocale()

    const isHorizontal = computed(
      () => props.direction === 'rtl' || props.direction === 'ltr'
    )
    const drawerSize = computed(() => addUnit(props.size))

    // bugfix: 修复safari浏览器14版本的问题
    const transition = computed(() => (isSafari() ? '' : 'transition'))

    const titleSize = computed(
      () => props.titleSize || globalConfig.value?.titleSize || 'normal'
    )
    return {
      ...useDialog(props, drawerRef),
      drawerRef,
      focusStartRef,
      isHorizontal,
      drawerSize,
      transition,
      titleSize,
      ns,
      t,
    }
  },
})
</script>
